import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Badge } from '@/components/common/Badge';
import { formatAmount } from '@/utils/format';
import type { ApprovalStatus, InvoiceStatus, PaymentStatus, RefundStatus } from '@/types/finance';

/**
 * The pieces every finance screen needs.
 *
 * Money is right-aligned and tabular everywhere, without exception. A column of
 * figures that do not line up on the decimal point cannot be scanned for the odd
 * one out, which is the single thing a bursar does with a column of figures.
 */

/** A monetary cell: aligned, tabular, and dimmed when it is zero. */
export function Money({
  amount,
  className,
  bold = false,
}: {
  amount: number | null | undefined;
  className?: string;
  bold?: boolean;
}) {
  const zero = amount === null || amount === undefined || amount === 0;
  return (
    <span
      className={clsx(
        'block text-right tabular-nums',
        bold && 'font-semibold',
        zero && 'text-subtle',
        className,
      )}
    >
      {formatAmount(amount)}
    </span>
  );
}

/** A labelled figure, for the strip along the top of a screen. */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'danger' | 'success' | 'muted';
}) {
  return (
    <div className="border border-line bg-surface px-4 py-3.5">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-subtle">
        {label}
      </span>
      <span
        className={clsx(
          'mt-1 block text-[24px] leading-none tabular-nums',
          tone === 'danger' && 'text-danger',
          tone === 'success' && 'text-success',
          tone === 'muted' ? 'text-muted' : 'text-ink',
        )}
      >
        {value}
      </span>
      {hint !== undefined && <span className="mt-1.5 block text-[11.5px] text-muted">{hint}</span>}
    </div>
  );
}

const INVOICE_TONES: Record<InvoiceStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'danger',
  CANCELLED: 'neutral',
  REFUNDED: 'neutral',
};

const PAYMENT_TONES: Record<PaymentStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'neutral',
};

const REFUND_TONES: Record<RefundStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  REQUESTED: 'info',
  APPROVED: 'warning',
  PROCESSED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

const APPROVAL_TONES: Record<ApprovalStatus, 'info' | 'success' | 'danger'> = {
  PENDING: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
};

function pill(tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger', label: string) {
  return (
    <Badge tone={tone}>
      {/* The dot is the second channel: the word carries the meaning, the colour
          only reinforces it, and neither is lost in greyscale. */}
      <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
      {label}
    </Badge>
  );
}

const humanise = (value: string) => value.replaceAll('_', ' ').toLowerCase();

export const InvoiceStatusBadge = ({ status }: { status: InvoiceStatus }) =>
  pill(INVOICE_TONES[status] ?? 'neutral', humanise(status));

export const PaymentStatusBadge = ({ status }: { status: PaymentStatus }) =>
  pill(PAYMENT_TONES[status] ?? 'neutral', humanise(status));

export const RefundStatusBadge = ({ status }: { status: RefundStatus }) =>
  pill(REFUND_TONES[status] ?? 'neutral', humanise(status));

export const ApprovalBadge = ({ status }: { status: ApprovalStatus }) =>
  pill(APPROVAL_TONES[status] ?? 'neutral', humanise(status));

/**
 * A horizontal bar, for the comparisons a finance dashboard actually needs.
 *
 * Deliberately not a chart library. These are single-series magnitude
 * comparisons where the bar is the whole encoding, the labels sit beside the
 * data rather than in a legend, and the value is written out — so it reads in
 * greyscale, in print, and to a screen reader.
 */
export function BarRow({
  label,
  sublabel,
  value,
  max,
  formatted,
}: {
  label: string;
  sublabel?: string;
  value: number;
  max: number;
  formatted: string;
}) {
  const width = max <= 0 ? 0 : Math.max((value / max) * 100, value > 0 ? 2 : 0);
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="truncate text-[13px] text-ink">
          {label}
          {sublabel !== undefined && <span className="ml-1.5 text-[11px] text-subtle">{sublabel}</span>}
        </span>
        <span className="shrink-0 text-[13px] tabular-nums text-ink">{formatted}</span>
      </div>
      <div className="mt-1.5 h-2 w-full bg-surface-3">
        <div className="h-full bg-accent" style={{ width: `${width}%` }} aria-hidden="true" />
      </div>
    </div>
  );
}
